/**
 * Tenant-configurable HIRA risk bands (EHSMS-F-12 / ISO 45001).
 *
 * Single client-side source of truth for mapping a 5x5 risk score (1-25)
 * to a band {min, max, label, color, acceptable}. Bands come from
 * GET /hira/risk-band-config; DEFAULT_RISK_BANDS mirrors the backend
 * defaults (services/risk_bands.py) so unconfigured tenants render
 * identically before the fetch resolves.
 *
 * Display-only: the server recomputes risk levels and enforces approval
 * gating regardless of what the client shows.
 */
import axios from 'axios';
import { API } from '../App';

export const DEFAULT_RISK_BANDS = [
  { min: 1, max: 5, label: 'Low', color: '#22C55E', acceptable: true },
  { min: 6, max: 12, label: 'Medium', color: '#EAB308', acceptable: true },
  { min: 13, max: 20, label: 'High', color: '#F97316', acceptable: false },
  { min: 21, max: 25, label: 'Critical', color: '#EF4444', acceptable: false },
];

// Colors for risk_level labels stored before a tenant's bands were
// renamed (e.g. archived assessments labelled "Critical" after the tenant
// switched to an "Extreme" top band).
const LEGACY_LABEL_COLORS = {
  low: '#22C55E',
  medium: '#EAB308',
  high: '#F97316',
  critical: '#EF4444',
  extreme: '#EF4444',
};

let _cache = null; // { bands, is_customized, default_bands, can_manage }
let _pending = null;

/**
 * Fetch the tenant band config (cached per page load).
 * Returns {bands, is_customized, default_bands, can_manage}.
 * Falls back to defaults (can_manage=false — fail closed) on error.
 */
export const fetchRiskBandConfig = async (force = false) => {
  if (_cache && !force) return _cache;
  if (_pending && !force) return _pending;
  _pending = axios
    .get(`${API}/hira/risk-band-config`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
    .then((res) => {
      _cache = res.data;
      return _cache;
    })
    .catch(() => ({
      bands: DEFAULT_RISK_BANDS,
      is_customized: false,
      default_bands: DEFAULT_RISK_BANDS,
      can_manage: false,
    }))
    .finally(() => {
      _pending = null;
    });
  return _pending;
};

/** Invalidate the cache (call after saving band config). */
export const clearRiskBandCache = () => {
  _cache = null;
};

/** Band a score falls into; out-of-range resolves to the most severe band. */
export const resolveBand = (score, bands) => {
  const list = bands && bands.length ? bands : DEFAULT_RISK_BANDS;
  if (Number.isFinite(score)) {
    const hit = list.find((b) => score >= b.min && score <= b.max);
    if (hit) return hit;
  }
  return list[list.length - 1];
};

/** Band label for a score. */
export const resolveBandLabel = (score, bands) => resolveBand(score, bands).label;

/**
 * Hex color for a stored risk_level label. Prefers the current band set;
 * falls back to the legacy label map so archived assessments still render.
 */
export const getBandColorForLabel = (label, bands) => {
  const list = bands && bands.length ? bands : DEFAULT_RISK_BANDS;
  const match = list.find(
    (b) => b.label.toLowerCase() === String(label || '').toLowerCase()
  );
  if (match) return match.color;
  return LEGACY_LABEL_COLORS[String(label || '').toLowerCase()] || '#6B7280';
};

/** True when the label (or score) is in an acceptable band. */
export const isAcceptable = (score, bands) => resolveBand(score, bands).acceptable;
